import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import {
  Body,
  Col,
  Colors,
  Title,
  Menu,
  Dropdown,
  Button,
  Input,
  SegmentedControl,
  Icon,
} from '@cognite/cogs.js';
import {
  SchemaCollection,
  Rule,
  useGetMilestonesQuery,
  Ruleset,
  Milestone,
  MilestoneType,
} from 'types/dt-graphql-apollo-v2';
import { notification } from 'antd';
import {
  term,
  filterRulesetOrRuleBySearch,
  getEarliestDateFromMilestones,
  getLatestDateFromMilestones,
  getAllMilestoneCollections,
  getRulesetRules,
} from 'utils/project-ruleset';
import { Spinner, VerticalDivider } from 'components';

import { useAccessControl } from 'hooks/useAccessControl';
import { groupedPermissionsList } from 'store/permissions';
import { FeatureFlags, useFeatureFlag } from 'utils/feature-flags';
import { Schema, useGetProjectQuery } from 'hooks/projects';
import { JobType } from 'hooks/useStartValidationJob';
import { RuleList } from './components/RuleList';
import { NewRuleDrawer } from './NewRuleDrawer';
import { NewRulesetDrawer } from './NewRulesetDrawer';
import { RulesetCards } from './components/RulesetCards';
import { RulesetList } from './components/RulesetList';
import { ViewRuleset } from './ViewRuleset';
import { RunRulesButton } from './components/RunRulesButton';
import { NewMilestoneDrawer } from './NewMilestoneDrawer';
import { MilestoneTypeSelectionModal } from './components/MilestoneTypeSelectionModal';
import { MilestoneDeleteModal } from './components/MilestoneDeleteModal';
import { MilestoneCards } from './components/MilestoneCards';
import { MilestoneInfo } from './components/MilestoneInfo';
import { CopyRulesModal } from './CopyRules/CopyRulesModal';
import { getDisabledRulesCount, isRuleDisabled } from './helpers';

enum ViewOption {
  Card = 'card',
  List = 'list',
}

export const ProjectRuleset = ({
  schemaCollection,
}: {
  schemaCollection: SchemaCollection;
}) => {
  const [search, setSearch] = useState<string>('');
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [viewOption, setViewOption] = useState<ViewOption>(ViewOption.Card);

  const [selectedMilestoneId, setSelectedMilestoneId] = useState<string>();
  const [showCreateMilestoneDrawer, setShowCreateMilestoneDrawer] =
    useState(false);
  const [showEditMilestoneDrawer, setShowEditMilestoneDrawer] = useState(false);
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [showMilestoneDeleteModal, setShowMilestoneDeleteModal] =
    useState(false);
  const [milestoneType, setMilestoneType] = useState<MilestoneType | null>(
    null
  );
  const [showMilestoneInfo, setShowMilestoneInfo] = useState<boolean>(false);

  const [showRuleDrawer, setShowRuleDrawer] = useState(false);
  const [selectedRuleToEdit, setSelectedRuleToEdit] = useState<Rule>();

  const [showRulesetDrawer, setShowRulesetDrawer] = useState(false);
  const [showRulesetView, setShowRulesetView] = useState(false);
  const [selectedRulesetToEdit, setSelectedRulesetToEdit] = useState<Ruleset>();
  const [selectedRulesetIdToView, setSelectedRulesetIdToView] = useState<
    Ruleset['externalId'] | null
  >();

  const hasAccess = useAccessControl();
  const enabledCopyRules = useFeatureFlag(FeatureFlags.enabledCopyRules);

  // Get project/digital twin details
  const { data: projectData } = useGetProjectQuery(schemaCollection.refId);

  // Get all milestones
  const {
    data: milestonesData,
    loading: loadingMilestones,
    refetch: refetchMilestones,
  } = useGetMilestonesQuery({
    variables: {
      projectId: schemaCollection.externalId,
    },
  });

  const currentSchemas = projectData?.Project[0]?.schemas || [];

  // Milestones
  const milestones =
    (milestonesData?.listMilestone?.items as Milestone[]) || [];
  const selectedMilestone = milestones.find(
    (m) => m.externalId === selectedMilestoneId
  );
  const selectedMilestoneToEdit = showEditMilestoneDrawer
    ? selectedMilestone
    : undefined;
  const earliestDateFromMilestones = getEarliestDateFromMilestones(milestones);
  const latestDateFromMilestones = getLatestDateFromMilestones(milestones);
  const defaultMilestonesCount =
    getAllMilestoneCollections(milestones)?.length || 0;

  // Access
  const canEditCollection = hasAccess(['Update_Milestone']);
  const canDeleteCollection = hasAccess(['Delete_Milestone']);
  const canCreateRuleset =
    hasAccess(groupedPermissionsList.CAN_CREATE_LINKED_RULESET) &&
    selectedMilestone;
  const canCreateRule =
    hasAccess(groupedPermissionsList.CAN_CREATE_LINKED_RULE) &&
    selectedMilestone;
  const canRunRules = hasAccess(['Add_Rule_ruleRunStatus']);

  // Rules
  const rules = (selectedMilestone?.rules?.items as Rule[]) || [];
  const enabledRules = rules.filter((r) => !isRuleDisabled(r));
  const rulesCount = rules.length;
  const disabledRulesCount = getDisabledRulesCount(rules);
  const independentRules = rules.filter((r) => r?.rulesets?.items.length === 0);
  const filteredRules = filterRulesetOrRuleBySearch(
    search,
    independentRules
  ) as Rule[];

  // Rulesets
  const rulesets = (selectedMilestone?.rulesets?.items as Ruleset[]) || [];
  const rulesetsCount = rulesets.length;
  const filteredRulesets = filterRulesetOrRuleBySearch(
    search,
    rulesets
  ) as Ruleset[];
  const selectedRulesetToView = rulesets?.find(
    (r) => r.externalId === selectedRulesetIdToView
  );

  // Select the first milestone after initial data load
  useEffect(() => {
    if (milestonesData && !selectedMilestoneId) {
      const newMilestoneId = milestonesData.listMilestone?.items[0]?.externalId;
      setSelectedMilestoneId(newMilestoneId);
    }
  }, [milestonesData]);

  // Collapse info if there is no selected milestone
  useEffect(() => {
    if (!selectedMilestone) setShowMilestoneInfo(false);
  }, [selectedMilestone]);

  const onDeleteRuleComplete = () => {
    notification.success({
      message: `Successfully deleted the ${term.rule}`,
    });
  };

  const onEditRule = (rule: Rule) => {
    setSelectedRuleToEdit(rule);
    setShowRuleDrawer(true);
  };

  const resetRuleDrawer = () => {
    setShowRuleDrawer(false);
    setSelectedRuleToEdit(undefined);
  };

  const onDeleteRulesetComplete = () => {
    setSelectedRulesetIdToView(null);
    notification.success({
      message: `Successfully deleted the ${term.ruleset}`,
    });
  };

  const onEditRuleset = (ruleset: Ruleset) => {
    setSelectedRulesetToEdit(ruleset);
    setShowRulesetDrawer(true);
  };

  const resetRulesetDrawer = () => {
    setShowRulesetDrawer(false);
    setSelectedRulesetToEdit(undefined);
  };

  const onViewRuleset = (ruleset: Ruleset) => {
    setSelectedRulesetIdToView(ruleset.externalId);
    setSelectedRulesetToEdit(undefined);
    setShowRulesetView(true);
    setShowRulesetDrawer(false);
  };

  const resetRulesetView = () => {
    setShowRulesetView(false);
    setSelectedRulesetIdToView(null);
    setSelectedRulesetToEdit(undefined);
  };

  // Opens newly created milestone
  const onUpsertMilestoneSuccess = (id?: string) => {
    setSelectedMilestoneId(id);
  };

  const resetMilestoneDrawer = () => {
    setShowCreateMilestoneDrawer(false);
    setShowEditMilestoneDrawer(false);
  };

  const handleMilestoneCreation = (type: MilestoneType) => {
    setMilestoneType(type);
    setShowMilestoneModal(false);
    setShowCreateMilestoneDrawer(true);
  };

  // Resets current milestone to show a new default after deletion
  const onDeleteMilestoneComplete = () => {
    setSelectedMilestoneId('');
    setShowMilestoneDeleteModal(false);
  };

  const onEditMilestone = () => {
    if (selectedMilestone?.milestoneType) {
      setMilestoneType(selectedMilestone.milestoneType as MilestoneType);
      setShowMilestoneModal(false);
      setShowEditMilestoneDrawer(true);
    }
  };

  const onCopyRulesSuccess = () => {
    setShowCopyDialog(false);
    refetchMilestones();
  };

  if (milestones.length === 0 && loadingMilestones) {
    return (
      <StyledPage>
        <StyledCol>
          <Spinner />
        </StyledCol>
      </StyledPage>
    );
  }

  const renderHeader = () => (
    <>
      <Col>
        <StyledTitle level={3}>Rulesets</StyledTitle>
      </Col>
      <StyledInlineFlex>
        <Title level={4}>Timeline:</Title>
        <StyledBodyGroup level={2}>
          <span>
            <StyledSpanBold>{defaultMilestonesCount}</StyledSpanBold>{' '}
            {defaultMilestonesCount !== 1 ? term.milestones : term.milestone}
          </span>
          {earliestDateFromMilestones && latestDateFromMilestones && (
            <>
              <VerticalDivider />
              <span>
                {earliestDateFromMilestones} to {latestDateFromMilestones}
              </span>
            </>
          )}
        </StyledBodyGroup>
      </StyledInlineFlex>
    </>
  );

  const renderSelectedMilestoneInfo = () => (
    <MilestoneInfo
      selectedMilestone={selectedMilestone}
      milestones={milestones}
      show={showMilestoneInfo}
      rulesetsCount={rulesetsCount}
      rulesCount={rulesCount}
    />
  );

  const renderMilestoneCards = () => (
    <MilestoneCards
      changeMilestone={setSelectedMilestoneId}
      milestones={milestones}
      onCreateNewMilestone={() => setShowMilestoneModal(true)}
      onCreateNewMilestoneWithType={handleMilestoneCreation}
      selectedMilestoneId={selectedMilestoneId}
    />
  );

  const renderContentHeader = () => {
    if (!selectedMilestone) return null;

    return (
      <>
        <StyledRowHeaderSmall>
          <StyledCol>
            <StyledInlineFlex>
              <Title level={4}>{selectedMilestone.name}</Title>
              <StyledBodyGroup level={2}>
                <StyledSpanBold>{rulesetsCount}</StyledSpanBold>{' '}
                {rulesetsCount !== 1 ? term.rulesets : term.ruleset}
                <VerticalDivider />
                <StyledSpanBold>{rulesCount}</StyledSpanBold>{' '}
                {rulesCount !== 1 ? term.rules : term.rule}
                <VerticalDivider />
                <StyledSpanBold>
                  {disabledRulesCount}
                </StyledSpanBold> disabled{' '}
                {disabledRulesCount !== 1 ? term.rules : term.rule}
              </StyledBodyGroup>
            </StyledInlineFlex>
          </StyledCol>
          <StyledColButtons>
            {canRunRules && (
              <RunRulesButton
                buttonIcon="Play"
                buttonText="Generate report"
                entityId={selectedMilestone.externalId}
                milestone={selectedMilestone}
                rules={enabledRules}
                schemaCollectionRefId={schemaCollection.refId}
                type={JobType.COLLECTION}
              />
            )}

            {(canCreateRuleset || canCreateRule) && (
              <Dropdown
                content={
                  <Menu>
                    {canCreateRuleset && (
                      <Menu.Item onClick={() => setShowRulesetDrawer(true)}>
                        <div>New {term.ruleset}</div>
                      </Menu.Item>
                    )}
                    {canCreateRule && (
                      <Menu.Item onClick={() => setShowRuleDrawer(true)}>
                        <div>New {term.rule}</div>
                      </Menu.Item>
                    )}
                    {canCreateRule && enabledCopyRules && (
                      <>
                        <Menu.Divider />
                        <Menu.Item
                          onClick={() => setShowCopyDialog(true)}
                          icon="PanelLeft"
                          iconPlacement="left"
                        >
                          <div>Add existing rules</div>
                        </Menu.Item>
                      </>
                    )}
                  </Menu>
                }
              >
                <Button icon="Add" type="primary">
                  Create
                </Button>
              </Dropdown>
            )}

            {selectedMilestone &&
              (canDeleteCollection || canEditCollection) && (
                <Dropdown
                  content={
                    <Menu>
                      {canEditCollection && (
                        <Menu.Item
                          onClick={onEditMilestone}
                          aria-label={`Edit ${term.milestone}`}
                          icon="Edit"
                          iconPlacement="left"
                        >
                          <span>Edit {term.milestone}</span>
                        </Menu.Item>
                      )}

                      {canDeleteCollection && (
                        <Menu.Item
                          onClick={() => setShowMilestoneDeleteModal(true)}
                          aria-label={`Delete ${term.milestone}`}
                          icon="Delete"
                          iconPlacement="left"
                          destructive
                        >
                          <span>Delete {term.milestone}</span>
                        </Menu.Item>
                      )}
                    </Menu>
                  }
                >
                  <Button
                    icon="EllipsisVertical"
                    type="ghost"
                    aria-label={`${term.Milestone} options`}
                  />
                </Dropdown>
              )}
            {selectedMilestone && (
              <Button
                icon={showMilestoneInfo ? 'ChevronUp' : 'ChevronDown'}
                type="ghost"
                aria-label="Expand"
                onClick={() => setShowMilestoneInfo(!showMilestoneInfo)}
              />
            )}
          </StyledColButtons>
        </StyledRowHeaderSmall>
      </>
    );
  };

  const renderContent = () => (
    <StyledContent>
      <StyledSearchRow>
        <StyledInput
          placeholder="Search &hellip;"
          icon="Search"
          iconPlacement="left"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
        <SegmentedControl
          currentKey={viewOption}
          onButtonClicked={(view) => setViewOption(view as ViewOption)}
        >
          <SegmentedControl.Button
            key={ViewOption.Card}
            icon="Grid"
            aria-label="Card-view"
          />
          <SegmentedControl.Button
            key={ViewOption.List}
            icon="TableView"
            aria-label="List-view"
          />
        </SegmentedControl>
      </StyledSearchRow>

      <StyledFlexGroup>
        <Title level={5}>{term.Rulesets}</Title>
        {viewOption === ViewOption.Card && (
          <RulesetCards
            onDeleteRulesetComplete={onDeleteRulesetComplete}
            onEditRuleset={onEditRuleset}
            onViewRuleset={onViewRuleset}
            rules={rules}
            rulesets={filteredRulesets}
          />
        )}
        {viewOption === ViewOption.List && (
          <RulesetList
            onViewRuleset={onViewRuleset}
            onDeleteRulesetComplete={onDeleteRulesetComplete}
            onDeleteRuleComplete={onDeleteRuleComplete}
            onEditRuleset={onEditRuleset}
            onEditRule={onEditRule}
            rules={rules}
            rulesets={filteredRulesets}
            showPagination
          />
        )}
      </StyledFlexGroup>

      <StyledFlexGroup>
        <Title level={5}>Independent {term.rules}</Title>
        <RuleList
          rules={filteredRules}
          onDeleteRuleComplete={onDeleteRuleComplete}
          onEditRule={onEditRule}
          showPagination
        />
      </StyledFlexGroup>
    </StyledContent>
  );

  return (
    <>
      <StyledPage>
        <StyledCol>
          {showRulesetView && selectedRulesetToView ? (
            <ViewRuleset
              schemaCollectionRefId={schemaCollection.refId}
              ruleset={selectedRulesetToView}
              selectedMilestone={selectedMilestone}
              milestones={milestones}
              rules={getRulesetRules(selectedRulesetToView.externalId, rules)}
              onCancel={resetRulesetView}
              onCopyRulesSuccess={onCopyRulesSuccess}
              onEditRuleset={onEditRuleset}
              onEditRule={onEditRule}
              onCreateRule={() => setShowRuleDrawer(true)}
              onDeleteRuleComplete={onDeleteRuleComplete}
            />
          ) : (
            <>
              <StyledRowHeader>
                {renderHeader()}
                {renderMilestoneCards()}
              </StyledRowHeader>
              {renderContentHeader()}
              {renderSelectedMilestoneInfo()}
              {renderContent()}
            </>
          )}
        </StyledCol>

        <NewRuleDrawer
          visible={showRuleDrawer}
          resetDrawer
          selectedRule={selectedRuleToEdit}
          currentSchemas={currentSchemas as Schema[]}
          schemaId={schemaCollection.externalId}
          rulesets={rulesets}
          selectedMilestone={selectedMilestone}
          selectedRuleset={selectedRulesetToView}
          onCancel={resetRuleDrawer}
        />
        <NewRulesetDrawer
          visible={showRulesetDrawer}
          resetDrawer
          selectedRuleset={selectedRulesetToEdit}
          currentSchemas={currentSchemas as Schema[]}
          selectedMilestone={selectedMilestone}
          schemaId={schemaCollection.externalId}
          onCancel={resetRulesetDrawer}
        />
        <MilestoneTypeSelectionModal
          visible={showMilestoneModal}
          onSelectMilestoneType={handleMilestoneCreation}
          onCancel={() => setShowMilestoneModal(false)}
        />
        <MilestoneDeleteModal
          visible={showMilestoneDeleteModal}
          selectedMilestone={selectedMilestone}
          milestones={milestones}
          milestoneRulesets={rulesets}
          milestoneRules={rules}
          onDeleteMilestoneComplete={onDeleteMilestoneComplete}
          onCancel={() => setShowMilestoneDeleteModal(false)}
        />
        <NewMilestoneDrawer
          visible={showCreateMilestoneDrawer || showEditMilestoneDrawer}
          resetDrawer
          selectedMilestone={selectedMilestoneToEdit}
          milestones={milestones}
          milestoneType={milestoneType}
          schemaId={schemaCollection.externalId}
          onCancel={resetMilestoneDrawer}
          onSuccess={onUpsertMilestoneSuccess}
        />
      </StyledPage>

      <CopyRulesModal
        visible={showCopyDialog}
        destinationMilestone={selectedMilestone}
        milestones={milestones}
        onCancel={() => setShowCopyDialog(false)}
        onCopyRulesSuccess={onCopyRulesSuccess}
      />
    </>
  );
};

const StyledPage = styled.div`
  display: flex;
  flex: 1;
  overflow: auto;
  background: var(--cogs-bg-accent);
`;

const StyledRowHeader = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  flex-direction: column;
  margin-bottom: 0;
  margin-top: 0;
  width: 100%;
  padding: 2rem 2rem 1.5rem;
  border-bottom: 1px solid var(--cogs-bg-control--disabled);
  background: ${Colors['decorative--grayscale--white']};
`;

const StyledRowHeaderSmall = styled(StyledRowHeader)`
  padding: 1rem 2rem;
  flex-direction: row;
  justify-content: space-between;
  align-items: baseline;
`;

const StyledCol = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
`;

const StyledFlexGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const StyledColButtons = styled(StyledCol)`
  flex-direction: row;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const StyledInlineFlex = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 1rem;
`;

const StyledTitle = styled(Title)`
  margin-bottom: 0.5rem;
`;

const StyledBodyGroup = styled(Body)`
  display: inline-flex;
  gap: 0.5rem;
`;

const StyledSpanBold = styled.span`
  font-weight: 700;
`;

const StyledContent = styled.div`
  display: flex;
  flex: 1;
  gap: 1rem;
  flex-direction: column;
  padding: 2rem 2rem 1rem;
`;

const StyledSearchRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: 1rem;
  width: 100%;
`;

const StyledInput = styled(Input)`
  .cogs-input {
    border-radius: 6px;
  }
  .cogs-icon-Search {
    opacity: 0.25;
    left: 12px;
  }
  .input-clear-button {
    top: calc(50% - 28px / 2);
    right: 2px !important;
    width: 28px;
    height: 28px;
    .cogs-icon.input-content.input-icon.cogs-icon-Close {
      top: calc(50% - 12px / 2);
      width: 12px !important;
      height: 12px !important;
    }
  }
`;
